import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import DrawerView from '@app/src/components/Drawer/DrawerView'
import { AvatarSize } from '@app/src/components/Ui/Avatar'
import TextField from '@app/src/components/Ui/TextField'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import CompanyAvatar from '@app/src/pages/ResourceCollection/Collections/CellComponents/CompanyAvatar'
import { SimpleOrganization } from '@app/src/types/organizations'
import { NotificationSeverity, OrganizationSettings } from '@app/src/wf-constants'
import { Stack, Typography } from '@mui/material'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'
import AiScreeningStats from '../AiScreening/AiScreeningStats'
import { SolutionType } from '@app/src/types/resourceExplorer'

type CsmOrganizationSettings = {
  organizationId: number
  solutionType: SolutionType
  patchSettings: Partial<OrganizationSettings>
}

interface EditOrganizationCsmSettingsDrawerProps {
  closeDrawer: () => void
  organization: SimpleOrganization
}

const EditOrganizationCsmSettingsDrawer: React.FC<EditOrganizationCsmSettingsDrawerProps> = ({
  closeDrawer,
  organization,
}) => {
  const { watch, register, handleSubmit, errors, setValue } = useForm<OrganizationSettings>()
  const { formatMessage } = useIntl()
  const { showErrorNotification } = useErrorNotification()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const { mutateAsync, isLoading: isSaving } = useUpdateResource<CsmOrganizationSettings>()

  const { data: settings } = useFetchResource<OrganizationSettings>({
    key: [FetchKey.OrganizationCsmSettingsById, organization.id, organization.setting?.solutionType],
    endpoint: endpoints.organizationCsmSettingsById(
      organization.id,
      organization.setting?.solutionType ?? SolutionType.Default,
    ),
    options: { enabled: Boolean(organization.id), staleTime: 60000 },
  })

  const existingAllowedProviderScreenings = settings?.allowedProviderScreenings ?? 0
  const modifyAllowedProviderScreeningsBy = watch('allowedProviderScreenings')

  const onSubmit = async () => {
    const parsedModifyValue = Number(modifyAllowedProviderScreeningsBy)
    const newAllowedProviderScreenings = existingAllowedProviderScreenings + parsedModifyValue

    await mutateAsync(
      {
        url: endpoints.organizationCsmSettings,
        body: {
          organizationId: organization.id,
          solutionType: organization.setting?.solutionType ?? SolutionType.Default,
          patchSettings: {
            allowedProviderScreenings: newAllowedProviderScreenings,
          },
        },
      },
      {
        onError: error => {
          showErrorNotification({ requestError: error })
          setValue('allowedProviderScreenings', undefined)
        },
        onSuccess: () => {
          queryClient.invalidateQueries(FetchKey.OrganizationCollection)
          queryClient.invalidateQueries([
            FetchKey.OrganizationCsmSettingsById,
            organization.id,
            organization.setting?.solutionType,
          ])
          closeDrawer()
          setValue('allowedProviderScreenings', undefined)
          showSnackbar({
            message: formatMessage({ id: 'general.organizationSettingsSaved' }),
            severity: NotificationSeverity.success,
          })
        },
      },
    )
  }

  return (
    <DrawerView
      title="Manage AI Screenings Limit"
      buttons={[
        {
          variant: 'contained',
          label: (
            <Typography>
              {(modifyAllowedProviderScreeningsBy ?? 0) >= 0 ? 'Add' : 'Remove'}{' '}
              <Typography component="span" fontWeight="bold">
                {modifyAllowedProviderScreeningsBy}
              </Typography>{' '}
              screenings to the current limit
            </Typography>
          ),
          onClick: handleSubmit(onSubmit),
          disabled: isSaving || Number(modifyAllowedProviderScreeningsBy) === 0,
        },
      ]}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack mx={3}>
          <Stack alignItems="center" mb={6}>
            <CompanyAvatar organizationName={organization.name} size={AvatarSize.XXL} />
            <Typography variant="h5" mt={2} color="textPrimary">
              {organization.name}
            </Typography>
          </Stack>
          <Typography fontWeight="bold" gutterBottom variant="body1" color="textPrimary">
            Current values:
          </Typography>
          <AiScreeningStats
            allowedProviderScreenings={existingAllowedProviderScreenings}
            totalProviderScreened={settings?.totalProviderScreened ?? 0}
            remainingProviderScreenings={settings?.remainingProviderScreenings ?? 0}
          />

          <Typography fontWeight="bold" gutterBottom variant="body1" color="textPrimary" mt={6}>
            Add (+) or remove (-) to AI screenings limit:
          </Typography>
          <TextField
            hoveringLabel
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
            fullWidth
            name="allowedProviderScreenings"
            type="number"
            helperText={errors?.allowedProviderScreenings?.message}
            onClear={(): void => setValue('allowedProviderScreenings', '')}
          />
        </Stack>
      </form>
    </DrawerView>
  )
}

export default EditOrganizationCsmSettingsDrawer
